import { extractRasterImagesFromLatin1 } from './extractBinaryClipboardImages';
import { extractRtfClipboardImages } from './extractRtfClipboardImages';
import { sanitizePaste, type PasteSanitizeOptions } from './sanitizePaste';
import {
  inlinePhotoExtensionForMime,
  isAllowedInlinePhotoMime,
} from '../../constants/inlineImageMimeTypes';

export type MixedEmailPastePayload = {
  transactionId: string;
  html: string;
  images: File[];
  /**
   * Word/Excel put the picture that belongs in the document in `text/rtf`
   * and a bitmap of the whole selection on the clipboard. Those RTF pictures
   * stay even when the HTML is a table. The selection bitmap does not.
   */
  keepImagesBesideTable: boolean;
  /** Data-URI pictures already taken out of the HTML, before any RTF picts. */
  embeddedImageCount: number;
  /** First `text/rtf` read, before `text/html` can clear later flavors. */
  rtf: string;
  /** Type names read before any getData call. Later reads can come back empty. */
  clipboardTypes: string[];
  /** RTF byte length from the first read, before text/html is consumed. */
  rtfLength: number;
};

function hash(value: string): string {
  let result = 5381;
  for (let index = 0; index < value.length; index += 1) {
    result = (result * 33 + value.charCodeAt(index)) % 2147483647;
  }
  return result.toString(36);
}

const DATA_URI_IMAGE =
  /<img\b[^>]*\bsrc=["'](data:(image\/[a-zA-Z0-9.+-]+);base64,([^"']+))["'][^>]*>/gi;

function base64ToFile(mime: string, base64: string, name: string): File | null {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      // eslint-disable-next-line security/detect-object-injection -- typed-array write indexed by a plain numeric loop counter, not user-controlled key
      bytes[index] = binary.charCodeAt(index);
    }
    return new File([bytes], name, { type: mime });
  } catch {
    return null;
  }
}

/**
 * Harness gap fix (2026-08-04): most real-world "paste an email with inline
 * images" cases never populate `clipboardData.items` with separate file
 * entries at all — that only happens when the OS clipboard itself carries a
 * distinct image flavor (e.g. desktop Outlook). Copying rendered content out
 * of a browser tab (Gmail/Outlook web) instead embeds the image directly in
 * the HTML flavor as `<img src="data:image/...;base64,...">`. Without this
 * extraction, every inline image from that common case silently disappeared
 * before either candidate editor ever saw it — a harness gap, not a
 * Tiptap-vs-Quill difference. Remote `<img src="https://...">` references
 * (e.g. a vendor email's CDN-hosted logo) are a separate case, handled
 * separately: `sanitizePaste`'s `isAllowedRemoteImageSrc()` now
 * keeps those `<img>` tags as read-only display images instead of deleting
 * them — we don't fetch/re-host that URL's bytes (no CORS-safe way to do
 * that from here), we just stop stripping a tag the browser can already
 * render on its own, matching what native's paste path already did.
 */
function extractEmbeddedDataUriImages(html: string): File[] {
  const files: File[] = [];
  let match: RegExpExecArray | null;
  let index = 0;
  DATA_URI_IMAGE.lastIndex = 0;
  while ((match = DATA_URI_IMAGE.exec(html)) !== null) {
    const [, , mime, base64] = match;
    if (!isAllowedInlinePhotoMime(mime)) {
      continue;
    }
    const extension = inlinePhotoExtensionForMime(mime);
    const file = base64ToFile(
      mime,
      base64,
      `pasted-image-${index}.${extension}`,
    );
    if (file) {
      files.push(file);
      index += 1;
    }
  }
  return files;
}

export function readMixedEmailPaste(
  event: ClipboardEvent,
  options: PasteSanitizeOptions,
): MixedEmailPastePayload | null {
  const clipboard = event.clipboardData;
  if (!clipboard) return null;

  // Chrome drops later flavors after getData('text/html'). Read picture
  // bytes and the type list first, then the HTML.
  const clipboardTypes = readClipboardTypeNames(clipboard.types);
  const clipboardFileImages = collectClipboardImageFiles(clipboard);
  const rtf = clipboardRtf(clipboard);
  const rtfImages = rtf.length > 0 ? extractRtfClipboardImages(rtf) : [];
  const appleFlavorImages = extractAppleFlavorImages(clipboard);
  const rawHtml = clipboard.getData('text/html');
  const plainText = clipboard.getData('text/plain');
  const embeddedImages = rawHtml.trim()
    ? extractEmbeddedDataUriImages(rawHtml)
    : [];
  // Word's clipboard image beside a table is a picture of the whole
  // selection, often on a black page. The picture under the table is the
  // RTF \pict. Never keep that selection bitmap.
  const officeTable =
    isOfficeDocumentHtml(rawHtml) && pasteHtmlContainsTable(rawHtml);
  const preferDocumentPicts = officeTable && rtfImages.length > 0;
  const images = officeTable
    ? [...embeddedImages, ...rtfImages]
    : [
        ...clipboardFileImages,
        ...embeddedImages,
        ...(clipboardFileImages.length === 0 ? rtfImages : []),
        ...(clipboardFileImages.length === 0 && rtfImages.length === 0
          ? appleFlavorImages
          : []),
      ];

  if (!rawHtml.trim() && !plainText.trim() && images.length === 0) return null;

  const html = rawHtml.trim()
    ? sanitizePaste(rawHtml, {
        allowCodeBlocks: true,
        ...options,
      })
    : plainText
        .split(/\r?\n/)
        .map(line => `<p>${escapeHtml(line) || '<br>'}</p>`)
        .join('');
  const signature = [
    rawHtml,
    plainText,
    ...images.map(file => `${file.type}:${file.size}:${file.name}`),
  ].join('|');
  return {
    transactionId: `mixed-${hash(signature)}`,
    html,
    images,
    keepImagesBesideTable: preferDocumentPicts,
    embeddedImageCount: embeddedImages.length,
    rtf,
    clipboardTypes,
    rtfLength: rtf.length,
  };
}

/**
 * A Word picture that sits under a table must stay under it. Inserting at the
 * caret lands in the first cell, on top of the selection snapshot's black fill.
 */
export function appendImagesAfterLastTable(
  html: string,
  srcs: string[],
): string {
  if (srcs.length === 0) {
    return html;
  }
  const block = srcs
    .map(src => {
      const escaped = src.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      return `<p><img src="${escaped}" alt=""></p>`;
    })
    .join('');
  const end = html.toLowerCase().lastIndexOf('</table>');
  if (end < 0) {
    return `${html}${block}`;
  }
  const at = end + '</table>'.length;
  return `${html.slice(0, at)}${block}${html.slice(at)}`;
}

function pastedImgSrc(tag: string): string {
  const quoted = /\bsrc\s*=\s*(["'])([\s\S]*?)\1/i.exec(tag);
  if (quoted?.[2] != null) {
    return quoted[2].trim();
  }
  const unquoted = /\bsrc\s*=\s*([^\s>]+)/i.exec(tag);
  return (unquoted?.[1] ?? '').replace(/["']/g, '').trim();
}

/** Pictures the browser can already paint. Office slots are not in this count. */
export function countDisplayablePasteImages(html: string): number {
  const tags = html.match(/<img\b[^>]*>/gi) ?? [];
  return tags.filter(tag => {
    const src = pastedImgSrc(tag);
    return (
      /^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(src) ||
      /^https?:\/\//i.test(src) ||
      (/^blob:/i.test(src) && !/^blob:https:\/\/outlook\./i.test(src))
    );
  }).length;
}

/**
 * Word on Windows puts the photo in the HTML as a real image and again in
 * `text/rtf`. Appending every unused RTF pict paints that photo a second time.
 * Keep the HTML picture. Append an RTF pict only when the HTML does not
 * already show one.
 */
export function previewsToPlaceUnderTable(
  html: string,
  unusedPreviews: string[],
  embeddedImageCount: number,
  stitchedCount: number,
): string[] {
  const shown = countDisplayablePasteImages(html);
  if (shown === 0) {
    return unusedPreviews;
  }
  const embeddedRemaining = Math.max(0, embeddedImageCount - stitchedCount);
  const embeddedInUnused = Math.min(embeddedRemaining, unusedPreviews.length);
  const rtfPreviews = unusedPreviews.slice(embeddedInUnused);
  return rtfPreviews.slice(Math.min(shown, rtfPreviews.length));
}

/** Word, Excel, and PowerPoint clipboard HTML. Outlook mail does not use these. */
function isOfficeDocumentHtml(html: string): boolean {
  return (
    /ProgId\s+content\s*=\s*["']?(?:Word\.Document|Excel\.Sheet|PowerPoint\.Slide)/i.test(
      html,
    ) ||
    /schemas-microsoft-com:office:(?:word|excel)/i.test(html) ||
    /xmlns:w\s*=/i.test(html) ||
    /\bMsoTableGrid\b/i.test(html)
  );
}

function readClipboardTypeNames(
  types: DataTransfer['types'] | undefined,
): string[] {
  if (types == null) {
    return [];
  }
  const named: string[] = [];
  if (typeof (types as Iterable<unknown>)[Symbol.iterator] === 'function') {
    for (const entry of types as Iterable<unknown>) {
      if (typeof entry === 'string' && entry.length > 0) {
        named.push(entry);
      }
    }
  }
  if (named.length > 0) {
    return named;
  }
  const list = types as ArrayLike<string>;
  const length = Number(list.length ?? 0);
  for (let index = 0; index < length; index += 1) {
    const entry = list[index];
    if (typeof entry === 'string' && entry.length > 0) {
      named.push(entry);
    }
  }
  return named;
}

export function acceptPasteTransaction(
  recent: Map<string, number>,
  transactionId: string,
  now = Date.now(),
  windowMs = 750,
): boolean {
  for (const [id, timestamp] of recent) {
    if (now - timestamp > windowMs) recent.delete(id);
  }
  const previous = recent.get(transactionId);
  if (previous !== undefined && now - previous <= windowMs) return false;
  recent.set(transactionId, now);
  return true;
}

/**
 * Outlook cid: slots live on the raw HTML flavor. Screenshot / Finder paste
 * often has files + text/plain and no text/html — stitch the escaped payload
 * so caption text is not dropped.
 */
export function htmlToStitchOnPaste(
  rawHtml: string,
  payloadHtml: string,
): string {
  return rawHtml.trim() ? rawHtml : payloadHtml;
}

/** Image files on the OS clipboard (Finder/Explorer copy, Outlook desktop). */
function collectClipboardImageFiles(clipboard: DataTransfer): File[] {
  const fromItems = Array.from(clipboard.items)
    .filter(item => item.kind === 'file' && isAllowedInlinePhotoMime(item.type))
    .map(item => item.getAsFile())
    .filter((file): file is File => file !== null);
  if (fromItems.length > 0) {
    return fromItems;
  }
  return Array.from(clipboard.files ?? []).filter(file =>
    isAllowedInlinePhotoMime(file.type),
  );
}

/** True when paste HTML already carries an `<img>` (rehost that; don't also insert files). */
export function pasteHtmlContainsImg(html: string): boolean {
  return /<img\b/i.test(html);
}

/** data:/blob: imgs are already in HTML — rehost those; do not also insert File items. */
export function pasteHtmlContainsEmbeddedImage(html: string): boolean {
  return /<img\b[^>]*\bsrc=(["'])(?:data:|blob:)[^"']*\1/i.test(html);
}

/**
 * Finder / screenshot PNG paste: clipboard has image files and no embedded
 * data:/blob: `<img>`. `cid:` and empty src do **not** count — those tags are
 * stripped and would otherwise skip real clipboard files (Outlook thread paste).
 */
export function pasteHtmlContainsTable(html: string): boolean {
  return /<table\b/i.test(html);
}

/**
 * Word/Excel/Docs put a picture of the selection on the clipboard next to the
 * HTML table. That picture is not an Outlook cid slot — do not insert it.
 */
export function shouldDropClipboardSnapshotBesideTable(html: string): boolean {
  return pasteHtmlContainsTable(html) && !htmlHasUnresolvedOutlookImages(html);
}

export function shouldInsertClipboardImageFiles(
  html: string,
  imageFileCount: number,
): boolean {
  if (imageFileCount <= 0 || pasteHtmlContainsEmbeddedImage(html)) {
    return false;
  }
  if (shouldDropClipboardSnapshotBesideTable(html)) {
    return false;
  }
  return true;
}

/**
 * What is left of paste HTML once nothing a reader would see remains:
 * comments, `<style>`/`<script>`/`<title>`/`<head>` blocks, tags and `<br>`
 * gone, every spelling of the invisible spacers (`&nbsp;`, `&#160;`,
 * `&#x00A0;`, zero-width joiners, the narrow nbsp and BOM that Outlook-web,
 * Teams and Docs leave beside a picture) treated as whitespace. One
 * definition, shared by both "is this blank" questions below. Not the
 * `visiblePasteText` in `rtfTableToHtml.ts`: that one compares table text
 * (lower-cased, `&amp;` decoded) and keeps a different contract on purpose.
 */
export function pasteTextResidue(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(style|script|title|head)\b[\s\S]*?<\/\1\s*>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(
      /&(?:nbsp|thinsp|ensp|emsp|ZeroWidthSpace|zwnj|zwj|#0*(?:160|8203|8204|8205|8239|65279)|#x0*(?:A0|200B|200C|200D|202F|FEFF));/gi,
      ' ',
    )
    .replace(/<[^>]+>/g, '')
    .replace(/[\u00A0\u2009\u2002\u2003\u200B\u200C\u200D\u202F\uFEFF]/g, ' ')
    .trim();
}

/** Empty editor chrome (`<p><br></p>`) from a file-only clipboard paste. */
export function isBlankPasteHtml(html: string): boolean {
  if (pasteHtmlContainsImg(html)) {
    return false;
  }
  return pasteTextResidue(html).length === 0;
}

/**
 * A picture and nothing else: Chrome's "Copy image" shape (`<meta>` plus one
 * `<img>`), the one case where image files beside the HTML are known to BE
 * the pictures the HTML shows rather than extra attachments. An `<img>`
 * inside a comment is not a picture, so the test runs on the stripped HTML.
 */
export function isImageOnlyPasteHtml(html: string): boolean {
  const uncommented = html.replace(/<!--[\s\S]*?-->/g, '');
  return (
    pasteHtmlContainsImg(uncommented) &&
    pasteTextResidue(uncommented).length === 0
  );
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

const APPLE_BINARY_CLIPBOARD_TYPES = [
  'com.apple.webarchive',
  'application/x-apple-webarchive',
  'text/rtfd',
  'com.apple.flat-rtfd',
];

function extractAppleFlavorImages(clipboard: DataTransfer): File[] {
  const files: File[] = [];
  for (const type of APPLE_BINARY_CLIPBOARD_TYPES) {
    try {
      files.push(
        ...extractRasterImagesFromLatin1(clipboard.getData(type) || ''),
      );
    } catch {
      // Chromium throws for types it does not expose.
    }
  }
  return files;
}

function clipboardRtf(clipboard: DataTransfer): string {
  for (const type of ['text/rtf', 'application/rtf', 'text/richtext']) {
    try {
      const value = clipboard.getData(type) ?? '';
      if (value.length > 0) {
        return value;
      }
    } catch {
      // Chromium throws for flavors it does not expose.
    }
  }
  return '';
}

/** Outlook HTML still pointing at cid: or Outlook-origin blob URLs (no bytes). */
export function htmlHasUnresolvedOutlookImages(html: string): boolean {
  return (
    /(?:src|originalsrc|background)\s*=\s*["']?(?:cid:|file:)/i.test(html) ||
    /url\(\s*["']?(?:cid:|file:)/i.test(html) ||
    /blob:https:\/\/outlook\./i.test(html)
  );
}

/**
 * After the paste event: Apple clipboard flavors, then Clipboard API image
 * items. Single-image copy already lands as File items.
 */
export async function supplementOutlookClipboardImages(
  clipboard: DataTransfer | null,
): Promise<File[]> {
  if (!clipboard) {
    return [];
  }
  const fromFlavor = extractAppleFlavorImages(clipboard);
  if (fromFlavor.length > 0) {
    return fromFlavor;
  }
  return readNavigatorClipboardImages();
}

async function readNavigatorClipboardImages(): Promise<File[]> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.read) {
    return [];
  }
  try {
    const items = await navigator.clipboard.read();
    const files: File[] = [];
    for (const item of items) {
      for (const type of item.types) {
        if (!type.startsWith('image/') || !isAllowedInlinePhotoMime(type)) {
          continue;
        }
        const blob = await item.getType(type);
        if (!blob || blob.size < 8) {
          continue;
        }
        files.push(
          new File(
            [blob],
            `pasted-clipboard-${files.length}.${inlinePhotoExtensionForMime(
              type,
            )}`,
            { type },
          ),
        );
      }
    }
    return files;
  } catch {
    return [];
  }
}
